import { useEffect, useSyncExternalStore } from 'react';
import { useState } from 'react';
import Setup from './components/Setup';
import Session from './components/Session';
import Review from './components/Review';
import Settings from './components/Settings';
import Help from './components/Help';
import Gate from './components/Gate';
import { controller, defaultConfig, type SessionConfig } from './lib/session-controller';
import { getToasts, subscribeToasts } from './lib/toast';
import { loadServerConfig, needsGate } from './lib/server';
import type { Accent, Mode } from './types';

type Modal = null | 'settings' | 'help';

export default function App() {
  const snap = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const toasts = useSyncExternalStore(subscribeToasts, getToasts);
  const [modal, setModal] = useState<Modal>(null);
  const [booted, setBooted] = useState(false);
  const [gated, setGated] = useState(false);

  // Load server config once at boot: decides whether the password gate shows.
  useEffect(() => {
    loadServerConfig().then(() => {
      setGated(needsGate());
      setBooted(true);
    });
  }, []);

  const phase = snap.phase;
  const inSession = phase !== 'idle' && phase !== 'review';

  function startSession(cfg: SessionConfig) {
    setModal(null);
    void controller.start(cfg);
  }
  function exitToSetup() {
    controller.reset();
  }

  // Keyboard map (handoff §12). Session keys only fire mid-session; modals and
  // text fields take precedence.
  useEffect(() => {
    function typingOrModal(): boolean {
      const el = document.activeElement as HTMLElement | null;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA');
      return !!typing || !!document.querySelector('.modal');
    }
    function onKey(e: KeyboardEvent) {
      if (typingOrModal()) {
        if (e.key === 'Escape') setModal(null);
        return;
      }
      if (!inSession) return;
      // Space (via code, reliable) or N advances during capture.
      if ((e.code === 'Space' || e.key.toLowerCase() === 'n') && phase === 'capturing') {
        e.preventDefault();
        controller.userNext();
        return;
      }
      if (e.key.toLowerCase() === 'r' && phase === 'asking') controller.repeat();
      else if (e.key === 'Escape') exitToSetup();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inSession, phase]);

  // Dev-gated automation surface (handoff §9). Off by default; on in dev or when
  // localStorage ielts.harness === '1'. Drives the same core the UI does.
  useEffect(() => {
    const enabled = import.meta.env.DEV || localStorage.getItem('ielts.harness') === '1';
    if (!enabled) return;
    (window as unknown as { harness: unknown }).harness = {
      start: (opts?: { mode?: Mode; accent?: Accent; topics?: string[]; count?: number }) =>
        controller.start(
          defaultConfig(opts?.mode ?? 'part1_drill', opts?.accent ?? 'en-GB', opts?.topics ?? [], opts?.count ?? 3),
        ),
      next: () => controller.userNext(),
      repeat: () => controller.repeat(),
      toggleReveal: () => controller.toggleReveal(),
      abort: () => controller.abort(),
      getSnapshot: () => controller.getSnapshot(),
      getMetrics: () => controller.getSnapshot().record?.summary ?? null,
      getRecord: () => controller.getSnapshot().record,
    };
    return () => {
      delete (window as unknown as { harness?: unknown }).harness;
    };
  }, []);

  if (!booted) {
    return (
      <div className="stage">
        <div className="frame" style={{ display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>Loading…</span>
        </div>
      </div>
    );
  }
  if (gated) {
    return <Gate onUnlock={() => setGated(false)} />;
  }

  return (
    <div className="stage">
      <div className={`frame${inSession ? ' screen-session-frame' : ''}`}>
        {phase === 'idle' && (
          <Setup onStart={startSession} onOpenSettings={() => setModal('settings')} onOpenHelp={() => setModal('help')} />
        )}
        {inSession && <Session onExit={exitToSetup} />}
        {phase === 'review' && snap.record && <Review record={snap.record} onAgain={exitToSetup} />}
      </div>

      {modal === 'settings' && <Settings onClose={() => setModal(null)} />}
      {modal === 'help' && <Help onClose={() => setModal(null)} />}

      <div className="toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.kind === 'err' ? ' err' : ''}`}>{t.msg}</div>
        ))}
      </div>
    </div>
  );
}
